import React, { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { closeInsightDrawer } from "portal/slices/insightDrawerSlice";
import InsightDetailRenderer from "./InsightDetailRenderer";

const InsightDrawer = () => {
  const dispatch = useDispatch();
  const { isOpen, insightData } = useSelector((state) => state.insightDrawer);

  const handleClose = useCallback(() => {
    dispatch(closeInsightDrawer());
  }, [dispatch]);

  // ESC key closes
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleClose]);

  if (!isOpen && !insightData) return null;

  const levelConfig = {
    green: { bg: "bg-green-50", text: "text-green-600", dot: "bg-green-500", label: "Positive" },
    red: { bg: "bg-red-50", text: "text-red-500", dot: "bg-red-500", label: "Risk" },
    yellow: { bg: "bg-orange-50", text: "text-orange-500", dot: "bg-orange-400", label: "Attention" },
  };

  const tagColors = {
    Growth: "text-green-600 bg-green-50",
    Risk: "text-red-500 bg-red-50",
    Warning: "text-orange-500 bg-orange-50",
    Optimization: "text-purple-600 bg-purple-50",
  };

  const level = levelConfig[insightData?.level] || levelConfig.yellow;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[998] animate-fadeIn"
          style={{ backgroundColor: "rgba(0,0,0,0.3)", backdropFilter: "blur(2px)" }}
          onClick={handleClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full z-[999] bg-surface-card shadow-2xl transition-transform duration-300 ease-in-out flex flex-col w-full sm:w-[460px] ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        {insightData && (
          <>
            <div className="px-5 py-4 border-b border-surface-border flex-shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2.5 h-2.5 rounded-badge ${level.dot}`} />
                    <span className={`text-xs font-medium ${level.text}`}>{level.label}</span>
                    {insightData.tag && (
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-badge ${tagColors[insightData.tag] || "text-content-secondary bg-surface-bg"}`}>
                        {insightData.tag}
                      </span>
                    )}
                  </div>
                  <h3 className="text-sm font-semibold text-content-primary leading-snug">
                    {insightData.message}
                  </h3>
                </div>
                <button
                  onClick={handleClose}
                  className="w-8 h-8 flex items-center justify-center rounded-button hover:bg-surface-hover transition-colors flex-shrink-0"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M1 1L13 13M1 13L13 1" stroke="#999" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto px-5 py-4">
              <InsightDetailRenderer insight={insightData} onClose={handleClose} />
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-surface-border flex-shrink-0">
              <p className="text-[10px] text-content-tertiary text-center">
                Auto-generated insight based on your clinic data
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default InsightDrawer;
